import { ApolloProvider, useMutation } from '@apollo/client';
import { SearchkitClient, SearchkitProvider } from '@searchkit/client';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FeatureSecurityProvider, SECURITY_ACTIONS } from '../common/auth/Security';
import { UserContext } from '../common/auth/UserContext';
import {
    BOOLEAN_STRINGS_CONSTANTS,
    DATA_GRID_CONSTANTS,
    FILTER_IDENTIFIERS,
    LIST_VIEW_CONSTANTS,
    SIDE_PANEL_IDS
} from '../common/constants/constants';
import { ENTITY_CONSTANTS } from '../common/constants/entityConstants';
import { FEATURE_CONSTANTS } from '../common/constants/featureConstants';
import { PREFERENCE_ENTITY_CONTEXTS_CONSTANT } from '../common/constants/preferenceConstants';
import { useDeepLinkListViewInitialFilter } from '../common/deepLink';
import { WORKSPACE_REPORT_ADAPTER_KEY } from '../common/generateReport/adapters/workspaceEntityReportAdapter';
import { useI18n } from '../common/i18n/I18n';
import { Icons } from '../common/icons/Icons';
import ListView from '../common/list/ListView';
import { searchClient, slimResultsQuery } from '../common/list/slimQuery';
import { useStyling } from '../common/Theme';
import { useConfigProductionScheduleRefetchNeeded } from '../common/utils/atoms/useConfigProductionScheduleRefetchNeeded';
import useHistory from '../common/utils/useHistory';
import HireResourceModal from '../hiringManager/HireResourceModal';
import { getIconForShowNames, getResourceTypes, useShowNames } from '../hiringManager/hireResourceUtils';
import { usePlanningSchedulerData } from '../scheduler/PlanningSchedulerDataContext';
import { deleteProductionResourcesFromSchedulerProductionData } from './../scheduler/scheduler';
import AddProductionButtonListView from './AddProductionButtonListView';
import { getWorkspaceDataCardPreference } from './DataList/ProductionDataListConfiguration/productionDataListConfigurationUtils';
import { ProductionDataListDataGrid } from './DataList/ProductionDataListDataGrid';
import { client, deleteQuery } from './helpers/production';
import {
    FILTER_FIELD_NAMES,
    Production_Field_Names,
    updateOptimisticQuickFilterState
} from './helpers/productionUtils';
import useProductionPreFilters from './helpers/useProductionPreFilters';
import { useListViewSort } from './hooks/useListViewSort';
import { STATE_TYPE } from './productionConstants';
import { ProductionDataGrid } from './ProductionDataGrid';
import ProductionImport from './ProductionImport';
import ProductionRankOrderList from './ProductionRankOrderList';

export enum ProductionModes {
    production = 'production',
    workspace = 'workspace'
}

export default function ProductionList() {
    const searchkit = new SearchkitClient({ itemsPerPage: DATA_GRID_CONSTANTS.defaultPageSize });
    const { application, activeOrganizationAccount, preferences, preferencesLoaded } = useContext(UserContext);
    const { workspacePreferenceValue } = getWorkspaceDataCardPreference(preferences || []);
    const { searchParams, changeSearch } = useHistory();
    const { isMobile } = useStyling();
    const preFilters = useProductionPreFilters();
    const productionMode = searchParams.get('productionMode');
    const workspaceView = searchParams.get('workspace_view');
    const isProductionMode = productionMode === ProductionModes.production;
    const [hireResourceVisible, setHireResourceVisible] = useState(false);
    const [selectedResType, setSelectedResType] = useState(undefined);
    const [controlledSidePanelId, setControlledSidePanelId] = useState<string | undefined>(undefined);
    const [customFilters, setCustomFilters] = useState({});
    const [optimisticStateForTagsFilter, setOptimisticStateForTagsFilter] = useState([]);
    const showNames = useShowNames();
    const resourceTypes = getResourceTypes();
    const [deleteProduction] = useMutation(deleteQuery, { client });
    const [_isSchedulerProductionRefetchNeeded, setIsSchedulerProductionRefetchNeeded] =
        useConfigProductionScheduleRefetchNeeded();
    const sortPref = workspacePreferenceValue?.sortWorkspacesBy;
    const isRankOrderView = useMemo(() => workspaceView === LIST_VIEW_CONSTANTS.rankOrderView, [workspaceView]);
    const { initialFilter, isInitialFilterReady, handleClearInitialFilter } = useDeepLinkListViewInitialFilter(
        ENTITY_CONSTANTS.production,
        undefined,
        productionMode !== ProductionModes.workspace
    );
    usePlanningSchedulerData();

    useEffect(() => {
        if (!isProductionMode && !productionMode) {
            changeSearch({ productionMode: ProductionModes.workspace });
        }
    }, [changeSearch, isProductionMode, productionMode]);

    // hook to determine sort model and custom variables for list view
    const { shouldApplyDefaultSort, customVariables, sortModel } = useListViewSort(
        sortPref,
        workspaceView,
        workspacePreferenceValue,
        customFilters
    );

    const projectStatusLabel = useI18n('filter.production.projectStatus');
    const tagsLabel = useI18n('filter.production.tags');

    const onDelete = (id: string) => {
        return new Promise<void>((resolve, reject) => {
            deleteProduction({
                variables: {
                    id: id
                },
                context: {
                    headers: {
                        ownerId: activeOrganizationAccount
                    }
                }
            })
                .then(() => {
                    console.log(`Deleted ${id}`);
                    deleteProductionResourcesFromSchedulerProductionData(
                        id,
                        activeOrganizationAccount,
                        setIsSchedulerProductionRefetchNeeded
                    );
                    resolve();
                })
                .catch((err) => {
                    console.log(`Failed to delete ${id}`);
                    console.log(err);
                    reject();
                });
        });
    };

    const isWorkspaceMode = productionMode === ProductionModes.workspace;

    return (
        <ApolloProvider client={searchClient} key={productionMode}>
            <SearchkitProvider client={searchkit}>
                {isWorkspaceMode ? (
                    <FeatureSecurityProvider featureName={FEATURE_CONSTANTS.productionWorkspace}>
                        <ListView
                            id={'workspace'}
                            shouldUseCentralizedSecurityFramework
                            feature={FEATURE_CONSTANTS.productionWorkspace}
                            entity={ENTITY_CONSTANTS.production}
                            preferenceEntityKey={PREFERENCE_ENTITY_CONTEXTS_CONSTANT.productionWorkspace}
                            uri={`${application.home}/workspace`}
                            query={slimResultsQuery(ENTITY_CONSTANTS.production, true, undefined, true)}
                            EntityTableRenderer={ProductionDataGrid}
                            EntityRankViewRenderer={ProductionRankOrderList}
                            EntityCardListRenderer={[
                                {
                                    icon: Icons.CardListIcon,
                                    label: LIST_VIEW_CONSTANTS.listView,
                                    component: ProductionDataListDataGrid
                                }
                            ]}
                            preFilters={preFilters}
                            immutableFilters={[
                                ...(preFilters || []),
                                {
                                    identifier: FILTER_IDENTIFIERS.managedProduction,
                                    value: BOOLEAN_STRINGS_CONSTANTS.true
                                },
                                {
                                    identifier: FILTER_FIELD_NAMES.state,
                                    value: STATE_TYPE.managed
                                }
                            ]}
                            initialFilter={initialFilter}
                            onClearInitialFilter={handleClearInitialFilter}
                            onDelete={onDelete}
                            enableImport={!isRankOrderView}
                            ImportView={ProductionImport}
                            shouldApplyDefaultSort={shouldApplyDefaultSort}
                            defaultSortModel={{
                                field: sortModel?.[0] || 'updatedOn',
                                sort: sortModel?.[1] || 'desc'
                            }}
                            createUri
                            defaultView={LIST_VIEW_CONSTANTS.listView}
                            enableSearch
                            quickFilters={[
                                { identifier: 'projectStatus', label: projectStatusLabel },
                                {
                                    identifier: Production_Field_Names.tags,
                                    label: tagsLabel,
                                    icon: Icons.LabelOutlined,
                                    optimisticState: optimisticStateForTagsFilter
                                }
                            ]}
                            updateQuickFilters={(results, updatedItem) =>
                                updateOptimisticQuickFilterState(
                                    Production_Field_Names.tags,
                                    results,
                                    updatedItem,
                                    setOptimisticStateForTagsFilter
                                )
                            }
                            containerSx={{ pr: isMobile ? 1 : 2 }}
                            quickAddButtonProps={{
                                enableQuickAddButtons: !isRankOrderView,
                                securityAction: SECURITY_ACTIONS.EDIT,
                                securityFeature: FEATURE_CONSTANTS.assessAvails,
                                onClickQuickAdd: (index: number) => {
                                    setHireResourceVisible(true);
                                    setSelectedResType(resourceTypes[index]);
                                },
                                setIcon: (index: number) => getIconForShowNames(index, resourceTypes),
                                showButtonNames: showNames,
                                sx: { width: 'unset' }
                            }}
                            entityReportAdapterKey={WORKSPACE_REPORT_ADAPTER_KEY}
                            slots={{ noRowsOverlay: AddProductionButtonListView }}
                            controlledSidePanelId={controlledSidePanelId}
                            onControlledSidePanelClose={() => setControlledSidePanelId(undefined)}
                            onOpenSidePanel={() => setControlledSidePanelId(SIDE_PANEL_IDS.statusReports)}
                            showConfigurationButtonForView={[
                                LIST_VIEW_CONSTANTS.listView,
                                LIST_VIEW_CONSTANTS.tableView
                            ]}
                            customVariables={customVariables}
                            setCustomVariables={setCustomFilters}
                            preferencesLoading={!preferencesLoaded}
                            skipQuery={!preferencesLoaded || !isInitialFilterReady}
                        />

                        {hireResourceVisible && (
                            <HireResourceModal
                                resourceTypes={resourceTypes}
                                setHireResourceVisible={setHireResourceVisible}
                                selectedResType={selectedResType}
                            />
                        )}
                    </FeatureSecurityProvider>
                ) : (
                    <FeatureSecurityProvider featureName={FEATURE_CONSTANTS.production}>
                        <ListView
                            id={'production'}
                            shouldUseCentralizedSecurityFramework
                            feature={FEATURE_CONSTANTS.production}
                            entity={ENTITY_CONSTANTS.production}
                            preferenceEntityKey={PREFERENCE_ENTITY_CONTEXTS_CONSTANT.production}
                            uri={`${application.home}/production`}
                            query={slimResultsQuery(ENTITY_CONSTANTS.production, true)}
                            EntityTableRenderer={ProductionDataGrid}
                            preFilters={preFilters}
                            immutableFilters={[
                                ...(preFilters || []),
                                { identifier: FILTER_IDENTIFIERS.allProductionsExceptPipeline, value: 'Production' },
                                { identifier: FILTER_IDENTIFIERS.excludeOwnedBranchedProductions, value: '' }
                            ]}
                            onDelete={onDelete}
                            enableSearch
                            enableImport
                            ImportView={ProductionImport}
                            defaultSortModel={{ field: 'updatedOn', sort: 'desc' }}
                            createUri
                            containerSx={{ pr: isMobile ? 1 : 2 }}
                        />
                    </FeatureSecurityProvider>
                )}
            </SearchkitProvider>
        </ApolloProvider>
    );
};
